import { useEffect, useMemo, useState } from "react";
import { useStore } from "@/provider/store";
import { ApiSnapCard } from "@/components/daylight-insight/apiCards";
import { CurrentStateSkeleton } from "@/components/skeleton/DashboardMetricsSkeleton";
import { useDashboardMetricsForPillar } from "./useDashboardMetricsForPillar";
import { MetricTrendsPanel } from "./MetricTrendsPanel";

/**
 * Current State — the live as-of-now snapshot.
 *
 * It is a server-granted dashboard like every other tab: `dashboard/config`
 * returns it as a `RoleDashboard` row (label "Current State" → pillar
 * `snapshot`), and its tiles come from POST `dashboard/dashboard-metric` for
 * that dashboard id. Nothing here reads the seeded metric layer any more.
 *
 * Unlike the pillar pages it carries NO filters — the snapshot is the whole
 * live position, so every server-side filter goes out as null (= all) and the
 * page shows exactly what the SP returns for the dashboard.
 *
 * Every metric the SP returns for the dashboard is rendered — the page does NOT
 * narrow to rows flagged `isCurrentState`, since the dashboard id already scopes
 * the result and filtering again would blank the page if the flag is unset.
 */
export function CurrentStatePage({ pillarK = "snapshot" }: { pillarK?: string }) {
  const s = useStore();
  // The snapshot dashboard's own spec — its label titles the page and its
  // `filters` drive the drill, exactly as on a pillar page. Nothing here
  // assumes the tab is called "Current State" or keyed `snapshot`.
  const spec = s.specFor(pillarK);

  const { category, loading, error, dashboardID } = useDashboardMetricsForPillar(pillarK);

  const metrics = useMemo(() => category?.metrics ?? [], [category]);
  const title = spec?.label ?? "Current State";

  // Publish what's on screen so the header's Export button writes these tiles
  // instead of falling back to the seeded workbook.
  const { setActiveDashboard } = s;
  useEffect(() => {
    setActiveDashboard(metrics.length ? { title, metrics } : null);
    return () => setActiveDashboard(null);
  }, [metrics, title, setActiveDashboard]);

  // Trends drill-down, same as the pillar pages: clicking a tile opens the
  // metric's trend/branch/payor views off the response already in hand. No page
  // filters, so the relation calls carry the date context alone.
  const [drillStack, setDrillStack] = useState<string[]>([]);
  const [drillTab, setDrillTab] = useState("chart");
  const relationBase = {
    serviceLineSystemID: s.serviceLineSystemID,
    startDate: s.range.start,
    endDate: s.range.end,
  };

  // `dashboardID == null` once the config has landed means the role simply
  // wasn't granted Current State — a permanent state, not a slow one.
  const notGranted = !s.configLoading && dashboardID == null;
  const pending = !notGranted && (loading || dashboardID == null || !s.serviceLineSystemID);

  return (
    <div className="pp">
      <div className="pp-head">
        <div>
          <div className="pp-title">{title}</div>
        </div>
        {s.dataRefreshDate && <span className="pp-grainchip">As of {s.dataRefreshDate}</span>}
      </div>
      {notGranted ? (
        <div style={{ color: "var(--ink-3)", fontSize: 14, marginTop: 20 }}>
          {title} metrics aren't part of your role's access.
        </div>
      ) : pending ? (
        <CurrentStateSkeleton />
      ) : error && !metrics.length ? (
        <div className="pp-card" style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}>
          {error}
        </div>
      ) : metrics.length ? (
        <div className="dl-grid" style={{ marginTop: 10 }}>
          {metrics.map((m) => (
            <ApiSnapCard
              key={m.code}
              metric={m}
              selected={drillStack[0] === m.code}
              onSelect={(metric) => {
                setDrillStack([metric.code]);
                setDrillTab("chart");
              }}
            />
          ))}
        </div>
      ) : (
        <div style={{ color: "var(--ink-3)", fontSize: 14, marginTop: 20 }}>
          No {title} metrics available.
        </div>
      )}
      {category && drillStack.length > 0 && (
        <MetricTrendsPanel
          category={category}
          stack={drillStack}
          setStack={setDrillStack}
          onClose={() => setDrillStack([])}
          tab={drillTab}
          setTab={setDrillTab}
          relationBase={relationBase}
          dashboardKey="snapshot"
          nowScope
        />
      )}
    </div>
  );
}
